import { useState, FC } from 'react'
import { useAuth } from '../../context/auth-context'
import { Table, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { SortOrder } from 'antd/lib/table'
import { useDateTime } from '../../shared/useDateTime'
import PerText from '../../components/custom_components/Text'
import { MeetingTag } from '../../components/custom_components/MeetingTag'
import PerFlexRow from '../../components/custom_components/FlexRow'
import { useReviewerToTakeOverMeeting } from '../../graphql/meeting/hooks/useReviewerToTakeOverMeeting'
import { PerAvatarPlus } from '../../components/custom_components/AvatarPlus'
import EmployeeMeetingStatus from './EmployeeMeetingStatus'
import { PerTableActionButtons } from '../../components/custom_components/TableActionButtons'
import { tableActionButtons } from '../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import { PerModal } from '../../components/custom_components/Modal'
import { MeetingPdfStatus, Meeting, MeetingStatus, MeetingType, Country } from '../../types'
import { getConclusionHrefByMeeting } from './util/hrefUtils'
import { MeetingStatusModal, MeetingStatusModalData } from '../../components/custom_components/MeetingStatusModal'
import { NameDisplayPreference } from '../../graphql/enum/name-display-preference.enum'
import { ForceConfirmationModal, ForceConfirmationRefetchContext } from './ForceConfirmationModal'
import { showNotification } from '../../components/custom_components/Notification/notification'

type SubordinateMeetingsTableProps = {
  tableData: Meeting[]
}

export const SubordinateMeetingsTable: FC<SubordinateMeetingsTableProps> = ({ tableData }) => {
  const { user } = useAuth()
  const { t } = useTranslation()
  const { formatDateTimeShort, compareDates } = useDateTime(user)
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [modalData, setModalData] = useState<any>()
  const [meetingStatusModalData, setMeetingStatusModalData] = useState<MeetingStatusModalData | null>(null)
  const [forceConfirmationModalData, setForceConfirmationModalData] = useState<Meeting | null>(null)
  const [reassignModalError, setReassignModalError] = useState<string | undefined>(undefined)

  const onModalCancel = () => {
    setModalOpen(false)
  }

  const onForceConfirmationModalCancel = () => {
    setForceConfirmationModalData(null)
  }

  const { tableActionButtonsRowWidth } = tableActionButtons()
  /*
  useEffect(() => {
    startPolling(10000)
    return () => {
      stopPolling()
    }
  }, [startPolling, stopPolling])
  */
  const setReviewer = useReviewerToTakeOverMeeting()
  const onClickTakeOver = async (meetingId: number) => {
    const { errors } = await setReviewer.employeeTakeOverMeeting(meetingId, user.employeeId)
    if (!!errors && !!errors.length) {
      setReassignModalError(t('global.notifications.error.description'))
    } else {
      showNotification({
        type: 'success',
        message: t('meetingView.allMeetings.confirmModal.notifications.reassign.success.title'),
        description: t('meetingView.allMeetings.confirmModal.notifications.reassign.success.description'),
      })
      setReassignModalError(undefined)
    }
    setModalOpen(false)
  }

  const renderName = (name, type: 'lastName' | 'firstName') => {
    if (type === 'firstName') {
      return name.chosenFirstName ? name.chosenFirstName : name.legalFirstName
    } else if (type === 'lastName') {
      return name.chosenLastName ? name.chosenLastName : name.legalLastName
    }
  }

  const getConclusionHrefByMeetingId = (id: number) => {
    const meeting = tableData.find((m) => m.id === id)
    return getConclusionHrefByMeeting(meeting)
  }

  const showMeetingModalStatus = (meeting: Meeting | undefined) => {
    const allowedMeetings = [MeetingType.Appraisal, MeetingType.AppraisalUpdate]
    const meetingStatus = [MeetingStatus.Prepared, MeetingStatus.Draft]
    return !!meeting && allowedMeetings.includes(meeting.type) && meetingStatus.includes(meeting.status)
  }

  const employeeTableHeader = <Tooltip title={t('employeeList.table.employeeTooltip')}>{t('meetingView.allMeetings.employee')}</Tooltip>

  const columns = [
    {
      key: 'date',
      title: t('meetingView.allMeetings.date'),
      className: 'minWidth-column-l',
      defaultSortOrder: 'ascend' as SortOrder,
      sorter: (meetingA: Meeting, meetingB: Meeting) => {
        return compareDates(meetingA.displayDate, meetingB.displayDate)
      },
      render: (tableData: Meeting) => <PerText>{formatDateTimeShort(tableData.displayDate)}</PerText>,
    },
    {
      key: 'appraisalSeason',
      title: t('meetingView.allMeetings.appraisalSeason'),
      className: 'minWidth-column-s',
      sorter: (meetingA: Meeting, meetingB: Meeting) => {
        const comparison = meetingA.appraisalSeason.year - meetingB.appraisalSeason.year
        if (comparison === 0) return compareDates(meetingA.displayDate, meetingB.displayDate)
        else return comparison
      },
      render: (tableData: Meeting) => <PerText>{tableData.appraisalSeason.year}</PerText>,
    },
    {
      key: 'type',
      title: t('meetingView.allMeetings.type'),
      className: 'minWidth-column-m',
      render: (tableData: Meeting) => <MeetingTag type={tableData.type} />,
    },
    {
      key: 'employee',
      title: employeeTableHeader,
      className: 'minWidth-column-xl',
      render: (tableData: Meeting) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={tableData.employee.id}
          firstName={renderName(tableData.employee, 'firstName')}
          lastName={renderName(tableData.employee, 'lastName')}
          infoText={tableData.employee.currentEmployeeLevel && t(`employee.contract.level.${tableData.employee.currentEmployeeLevel}`)}
          data-e2e-meeting-table-avatar
        />
      ),
    },
    {
      key: 'reviewer',
      title: t('meetingView.allMeetings.reviewer'),
      className: 'minWidth-column-xl',
      render: (tableData: Meeting) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={tableData.reviewer ? tableData.reviewer.id : ''}
          firstName={tableData.reviewer ? tableData.reviewer.firstName : undefined}
          lastName={tableData.reviewer ? tableData.reviewer.lastName : undefined}
          infoText={
            tableData.reviewer && tableData.reviewer.currentLevel ? t(`employee.contract.level.${tableData.reviewer.currentLevel}`) : ''
          }
          nameType={user.employeeId === (tableData.reviewer ? tableData.reviewer.id : '') ? 'you' : 'fullName'}
          data-e2e-meeting-table-avatar
        />
      ),
    },
    {
      key: 'status',
      title: t('meetingView.allMeetings.status'),
      className: 'minWidth-column-s',
      render: (tableData: Meeting) => (
        <>
          <PerFlexRow justify="start">
            <EmployeeMeetingStatus
              justify="start"
              firstName={renderName(tableData.employee, 'firstName')}
              meetingStatus={tableData.status}
              employeePrepared={tableData.employeePrepared}
            />
          </PerFlexRow>
        </>
      ),
    },
    {
      key: 'actions',
      title: t('table.actions'),
      className: 'textAlign-left',
      width: tableActionButtonsRowWidth(2),
      render: (tableData: Meeting) => {
        const pdfSuccess = tableData.conclusionPdfStatus === MeetingPdfStatus.FinishedSuccess
        return (
          <PerTableActionButtons
            justify="start"
            variant="pastel"
            buttons={[
              {
                showOnlyWhen: user.employeeId !== tableData!.reviewer!.id && tableData.status !== MeetingStatus.PendingConfirmation,
                icon: 'reassign_fill',
                color: 'success',
                tooltip: t('meetingView.allMeetings.assignToMe'),
                onClick: () => {
                  setModalOpen(true)
                  setModalData(tableData)
                },
              },
              {
                showOnlyWhen:
                  tableData.status === MeetingStatus.PendingConfirmation && tableData.isLoggedUserCompanyManagerOfAppraisalSeasonCompany,
                disabled: tableData.conclusionPdfStatus !== MeetingPdfStatus.FinishedSuccess,
                icon: 'chat_stroke',
                color: 'info',
                tooltip:
                  tableData.conclusionPdfStatus !== MeetingPdfStatus.FinishedSuccess
                    ? t('managerMeetings.tooltip.viewPdfDisabled')
                    : t('managerMeetings.tooltip.forceConfirmation'),
                onClick: () => {
                  setForceConfirmationModalData(tableData)
                },
              },
              {
                showOnlyWhen: showMeetingModalStatus(tableData),
                icon: 'view_fill',
                color: 'warning',
                tooltip: t('appraisalStatusModal.tooltip'),
                onClick: () =>
                  setMeetingStatusModalData({
                    employeeId: tableData.employee.id,
                    meetingId: tableData.id,
                    meetingType: tableData.type,
                    country: tableData.appraisalSeason.country as Country,
                  }),
              },
              {
                showOnlyWhen: tableData.status === MeetingStatus.PendingConfirmation,
                icon: 'view_fill',
                color: 'primary',
                tooltip: pdfSuccess ? t('managerMeetings.tooltip.viewPdf') : t('managerMeetings.tooltip.viewPdfDisabled'),
                asLink: getConclusionHrefByMeetingId(tableData.id),
                disabled: !pdfSuccess,
              },
            ]}
          />
        )
      },
    },
  ]

  return (
    <>
      {forceConfirmationModalData && (
        <ForceConfirmationModal
          companyId={forceConfirmationModalData.appraisalSeason.companyId}
          country={forceConfirmationModalData.appraisalSeason.country as Country}
          meetingId={forceConfirmationModalData.id}
          onClose={onForceConfirmationModalCancel}
          employeeFirstName={forceConfirmationModalData.employee.chosenFirstName || forceConfirmationModalData.employee.legalFirstName}
          refetchContext={ForceConfirmationRefetchContext.MeetingsLists}
        />
      )}
      {meetingStatusModalData && (
        <MeetingStatusModal
          employeeId={meetingStatusModalData.employeeId}
          meetingId={meetingStatusModalData.meetingId}
          meetingType={meetingStatusModalData.meetingType}
          onClose={() => {
            setMeetingStatusModalData(null)
          }}
          country={meetingStatusModalData.country}
        />
      )}
      {modalData && (
        <PerModal
          title={{
            heading: t('meetingView.allMeetings.confirmModal.heading'),
            subheading: t('meetingView.allMeetings.confirmModal.subheading', {
              reviewer: `${modalData.reviewer.firstName} ${modalData.reviewer.lastName}`,
              meetingType: t(`meetingTypes.${modalData.type}`),
              employee: `${renderName(modalData.employee, 'firstName')} ${renderName(modalData.employee, 'lastName')}`,
              date: formatDateTimeShort(modalData.date),
            }),
          }}
          size="small"
          centered
          visible={modalOpen}
          onCancel={onModalCancel}
          footer={{
            cancelBtn: { onClick: onModalCancel },
            confirmBtn: { onClick: () => onClickTakeOver(modalData.id) },
          }}
        >
          <PerText fontWeight="bold" mb="s">
            {t('meetingView.allMeetings.confirmModal.text1')}
          </PerText>
          <PerText mb="s">
            {t('meetingView.allMeetings.confirmModal.text2', {
              employee: renderName(modalData.employee, 'firstName'),
              reviewer: `${modalData.reviewer.firstName} ${modalData.reviewer.lastName}`,
            })}
          </PerText>
          {reassignModalError && (
            <PerText color="error" fontSize="m" mt="m">
              {reassignModalError}
            </PerText>
          )}
        </PerModal>
      )}

      <Table
        className="light small viewWithTabsAndInfoBox scroll-compensation"
        rowClassName={() => 'minHeight-row'}
        dataSource={tableData.map((o) => ({ ...o, key: o.id }))}
        columns={columns}
        pagination={false}
        scroll={{ y: true }}
        data-e2e-manager-meetings-table
      />
    </>
  )
}
